import * as Sentry from "@sentry/nextjs";

import { TERMINAL_STATUSES } from "src/constants/order.constant";
import type { OrderStatus } from "src/enums/order";

import type { CartDropReason } from "./order-freshness";
import type { OrderSessionSource, SessionStoreSummary } from "./order-session";

/**
 * Instrumentation for the single biggest hole in the funnel: customers who
 * upload photos and then lose the order.
 *
 * Measured on production over 14 days: ~8 orders/day are left PENDING with
 * photos still attached while the same visitor starts another order minutes
 * later, and 45 of those visitors re-uploaded 1,135 of the SAME filenames --
 * nobody does that on purpose, so they had lost sight of their photos. Only 27%
 * of the replacement orders ever convert.
 *
 * Two candidate mechanisms fit that evidence equally well from the database
 * alone, and they need different fixes:
 *
 *   A. the pre-flight check in handleCreateOrderAndUpload decides the resumed
 *      order is stale and silently discards it (including on a 401/403, which
 *      only means "could not prove ownership", not "this order is dead");
 *   B. the guest token / cookie is lost mid-session, the cart renders empty,
 *      and the customer re-uploads without any discard code running.
 *
 * These helpers tag both paths so Sentry can separate them. Report volume is
 * bounded by the events themselves (a few per day), not by render count.
 *
 * Everything here is best-effort: diagnostics must never break an upload, so
 * every function swallows its own errors.
 */

/** Why a resumed order was thrown away. Mirrors the branches at the call site. */
export type OrderDiscardReason =
  | "terminal-status" // order genuinely finalized -- correct to start fresh
  | "no-live-photos" // photos purged/never landed and nothing else in the cart
  | "http-401" // could NOT prove ownership -- not the same as "dead"
  | "http-403"
  | "http-404"; // order really is gone server-side

/**
 * What a failed pre-flight check means for the order we were about to resume.
 *
 * Before uploading, the client asks the backend "can this session use this
 * order?". That single question answers for BOTH kinds of customer, because
 * OrderOwnerGuard accepts either the bearer JWT or the guest token.
 *
 * A status here is a discard reason, or null to keep the order:
 *
 *   404       the order is gone server-side
 *   401 / 403 ownership could not be proven -- the upload cannot succeed on
 *             this order, so sending it anyway just loses the customer's batch
 *             after they have picked their photos (FOTO-NEXTJS-147 / 14P)
 *   no status a network error. A genuine "we don't know": keep the order,
 *             because guessing would orphan a live cart on a dropped
 *             connection.
 *
 * 401/403 were once kept, on the theory that they were a transient guest-token
 * read miss. That belonged to the old code, which built the order id and token
 * from separate stores and so could pair a real id with an empty token. It
 * cannot happen now: resolveOrderSession returns only a COMPLETE pair, so a
 * read miss produces no session and the pre-flight is never reached. A 401 on a
 * complete pair is a real answer. Discarding does not delete anything -- the
 * order stays on the server; we just stop pretending we can reach it.
 */
export function discardReasonForPreflightStatus(
  status: number | undefined,
): OrderDiscardReason | null {
  if (status === 404) return "http-404";
  if (status === 401) return "http-401";
  if (status === 403) return "http-403";
  return null;
}

export interface OrderDiscardContext {
  reason: OrderDiscardReason;
  orderId: string;
  /** Photos the customer is about to lose sight of. The cost of this event. */
  abandonedPhotoCount: number;
  /** Was a guest token available at all? Empty token is the prime suspect for 401. */
  hadGuestToken: boolean;
  /**
   * Which store supplied the order id AND its token -- they always come from
   * the same one now, so this names the layer that still had a whole session
   * (and, by elimination, the ones that lost it). See utils/order-session.ts.
   */
  tokenSource: OrderSessionSource | "none";
  httpStatus?: number;
}

/**
 * A resumed order was discarded and a fresh one will be created. This is the
 * event that strands the customer's photos, so it is reported even when the
 * discard was legitimate -- the rate of "correct" vs "wrong" discards is
 * exactly what we need to compare.
 */
/** Discards that provably cost the customer nothing, so they carry no signal. */
const HARMLESS_DISCARD_REASONS: ReadonlySet<OrderDiscardReason> = new Set([
  "no-live-photos", // nothing was on the order to lose
  "terminal-status", // order genuinely finished; starting fresh is correct
]);

export function reportOrderDiscarded(ctx: OrderDiscardContext): void {
  try {
    // Discarding an EMPTY order for a provably correct reason is the happy
    // path, not a defect. Reporting it anyway produced 51 identical events in
    // three days (all strandedPhotos=no), which pushed the issue to
    // "escalating" and buried the handful of events that represent real lost
    // work. The cases that still always report are the ones worth waking up
    // for: any discard that strands photos, and every 401/403/404 — a failure
    // to prove ownership is NOT proof the order was dead.
    if (
      ctx.abandonedPhotoCount === 0 &&
      HARMLESS_DISCARD_REASONS.has(ctx.reason)
    ) {
      return;
    }
    // warning, not error: some discards are correct behaviour. Severity here
    // would train everyone to ignore the alert.
    Sentry.captureMessage(`Order discarded before upload: ${ctx.reason}`, {
      level: ctx.abandonedPhotoCount > 0 ? "warning" : "info",
      tags: {
        area: "order-loss",
        discardReason: ctx.reason,
        hadGuestToken: String(ctx.hadGuestToken),
        tokenSource: ctx.tokenSource,
        // The one that matters: did this cost the customer real work?
        strandedPhotos: ctx.abandonedPhotoCount > 0 ? "yes" : "no",
      },
      extra: {
        orderId: ctx.orderId,
        abandonedPhotoCount: ctx.abandonedPhotoCount,
        httpStatus: ctx.httpStatus ?? null,
      },
    });
  } catch {
    // Diagnostics must never take an upload down with them.
  }
}

/** Why ensureOrderExists fell through to creating a brand-new order. */
export type OrderCreationReason =
  | "force-new" // caller explicitly asked to start over (recovery path)
  | "active-order-finalized" // previous order is paid/shipped -- correct to start fresh
  | "no-session"; // no order in state AND no usable cookie pair

export interface OrderCreationContext {
  /** An order id was known but could not be used -- candidate mechanism B. */
  hadPriorOrderId: boolean;
  /** The shared cookie still held an order id at creation time. */
  cookieHadOrderId: boolean;
  /** The shared cookie still held a guest token at creation time. */
  cookieHadGuestToken: boolean;
  /** Which flow asked for the order. */
  origin: "upload" | "ensureOrderExists";
  /** Why we fell through to creating one. Only set by ensureOrderExists. */
  reason?: OrderCreationReason;
  /**
   * Photos on the order that is about to be displaced, if we know of one.
   *
   * THIS IS THE DAMAGE FIELD. Production sequences look like:
   *   order A (11 photos) -> order B (0 photos) -> order C (12 photos, paid)
   * The empty B takes over as the active cart, the next upload correctly
   * discards B for having no photos, and A is stranded with the customer's
   * work. The discard of B reports harmlessly; the loss happens HERE, when B
   * is created on top of A.
   */
  displacedPhotoCount?: number;
  /**
   * WHICH order the photo count above was read from, and what state it was in.
   *
   * Without these two, displacedPhotoCount cannot be acted on, because two
   * completely different situations produce an identical report:
   *
   *   - a live PENDING/PROCESSING cart is thrown away  -> real loss, our bug
   *   - a PAID/SHIPPED order is replaced by a new one  -> correct behaviour;
   *     a finished order keeps its photos, so they get counted as "displaced"
   *     even though the customer lost nothing
   *
   * A 38-photo report on 2026-07-26 could not be classified for exactly this
   * reason. The status is emitted as a TAG so the two can be told apart at a
   * glance in Sentry instead of by reading individual events.
   */
  displacedOrderId?: string;
  displacedOrderStatus?: string;
  /**
   * Which of the four stores held which half, at the instant we gave up and
   * created a new order.
   *
   * The two booleans above look at the cookie alone, and the cookie is the store
   * mobile browsers evict. So the report could not tell "genuine first-time
   * visitor" from "a complete, live session was sitting in localStorage and the
   * reader never looked" — which is the mechanism behind 66d8ede2 and bca7d728,
   * and the one that costs customers their carts. See summariseSessionStores.
   */
  stores?: SessionStoreSummary;
}

/**
 * Is the displaced order one the customer had already finished with?
 *
 * TERMINAL_STATUSES is the same list checkout uses to decide an order can no
 * longer act as the cart, so this answers the question in exactly the terms the
 * code that triggers the displacement asked it. Reusing the list rather than
 * restating it is deliberate: the client and backend status contracts drifting
 * apart is what produced FOTO-NEXTJS-14C.
 *
 * An ABSENT status is not proof of anything, so it counts as live. Under-
 * reporting a real loss costs more than one extra false alarm, and the events
 * from before the tag existed (23-26 July) land here.
 */
function isFinishedOrderStatus(status: string | undefined): boolean {
  if (!status) return false;
  return (TERMINAL_STATUSES as readonly OrderStatus[]).includes(
    status as OrderStatus,
  );
}

/**
 * A brand-new order was created. Only reported when it looks SUSPICIOUS -- i.e.
 * there were traces of a previous order we failed to reuse. A first-time
 * visitor creating their first order is normal and is deliberately not sent.
 *
 * `cookieHadOrderId && !cookieHadGuestToken` is the signature of mechanism B:
 * half the session survived, so the restore in OrderProvider (which requires
 * BOTH) silently gave up and we started over on top of the customer's old cart.
 */

export function reportSuspiciousOrderCreation(ctx: OrderCreationContext): void {
  try {
    const halfSession = ctx.cookieHadOrderId !== ctx.cookieHadGuestToken;
    const photoCount = ctx.displacedPhotoCount ?? 0;
    const displacedOrderFinished = isFinishedOrderStatus(
      ctx.displacedOrderStatus,
    );
    // A finished order keeps its photos. Replacing it as the active cart is the
    // correct response to a customer who placed an order and came back -- they
    // lost nothing, so it is not a displacement in the sense that matters.
    // Deciding this on photoCount alone reported a settled COD order (134
    // photos, PAYMENT_ON_DELIVERY) as level=error data loss: FOTO-NEXTJS-148.
    const displaces = photoCount > 0 && !displacedOrderFinished;
    const replacesFinishedOrder = photoCount > 0 && displacedOrderFinished;
    // "Some store demonstrably held an order id, and no store held a usable
    // pair." The orphaned case, and the one the cookie booleans above cannot
    // see: an id left in localStorage or the URL while the cookie is empty
    // reads, to `halfSession`, exactly like a first-time visitor.
    const orphaned = ctx.stores?.orphanedOrderId === true;
    // A genuine first-time visitor creating their first order is normal and
    // must stay silent, or the signal drowns.
    if (
      !ctx.hadPriorOrderId &&
      !halfSession &&
      !orphaned &&
      !displaces &&
      !replacesFinishedOrder
    )
      return;

    Sentry.captureMessage(
      displaces
        ? "New order created ON TOP of an order with photos"
        : replacesFinishedOrder
          ? // Its own title, so Sentry groups it away from real loss instead of
            // burying it. Still reported: the ratio of correct to wrong
            // displacements is the whole point of this instrumentation, and
            // suppressing the correct half would leave it unmeasurable.
            "New order created over a finished order"
          : "New order created despite prior session traces",
      {
        // Displacing real work is the actual defect; the rest is context.
        level: displaces ? "error" : replacesFinishedOrder ? "info" : "warning",
        tags: {
          area: "order-loss",
          origin: ctx.origin,
          creationReason: ctx.reason ?? "unknown",
          // True => cookie pair was split, the restore path could not fire.
          halfSession: String(halfSession),
          // The one to alert on: real work lost, finished orders excluded.
          displacedPhotos: displaces ? "yes" : "no",
          // Why displacedPhotos is "no" despite a non-zero count below.
          displacedOrderFinished: String(displacedOrderFinished),
          // The discriminator. Replacing a PAID/SHIPPED order is correct and
          // costs the customer nothing (a finished order simply still has its
          // photos); replacing a PENDING/PROCESSING one is the actual defect.
          displacedOrderStatus: ctx.displacedOrderStatus ?? "unknown",
          // An id survived somewhere but no store held a usable pair.
          orphanedOrderId: String(orphaned),
          // One tag per store, so a blind spot ("storage=both, resolvedFrom=
          // none") and a session split across stores ("url=id-only,
          // cookie=token-only") are each a single glance rather than a
          // reconstruction. Four values each, so cardinality stays trivial.
          storeContext: ctx.stores?.context ?? "unknown",
          storeUrl: ctx.stores?.url ?? "unknown",
          storeCookie: ctx.stores?.cookie ?? "unknown",
          storeStorage: ctx.stores?.storage ?? "unknown",
          resolvedFrom: ctx.stores?.resolvedFrom ?? "unknown",
        },
        extra: {
          hadPriorOrderId: ctx.hadPriorOrderId,
          cookieHadOrderId: ctx.cookieHadOrderId,
          cookieHadGuestToken: ctx.cookieHadGuestToken,
          displacedPhotoCount: ctx.displacedPhotoCount ?? 0,
          // Names the order the count came from, so the claim can be checked
          // against the database instead of inferred from breadcrumbs.
          displacedOrderId: ctx.displacedOrderId ?? null,
          displacedOrderStatus: ctx.displacedOrderStatus ?? null,
          // Repeated in full so the event is readable without cross-referencing
          // the tags, and so a store value we later add is not silently lost.
          stores: ctx.stores ?? null,
        },
      },
    );
  } catch {
    // best effort
  }
}

/* -------------------------------------------------------------------------- */
/*  Silent wrong behaviour                                                     */
/*                                                                            */
/*  Everything below reports something that does NOT throw. That is the whole  */
/*  reason it exists: the cart and order-session bugs fixed in e9ec917b,       */
/*  c51fe42d, bca7d728 and 66d8ede2 all produced a wrong outcome through a     */
/*  successful code path — a cart quietly dropped, a duplicate order created   */
/*  next to a live one, photos written to the wrong parent group. Sentry       */
/*  recorded zero events across six occurrences of the last one, because       */
/*  nothing failed. Ordinary error reporting cannot catch any of them.         */
/*                                                                            */
/*  captureMessage, not captureException, throughout: an exception's value is  */
/*  its stack, and there is no stack worth having here — the interesting thing */
/*  is the decision, which lives in the tags. Titles are fixed strings so      */
/*  Sentry groups them; everything that varies is a tag or an extra.           */
/* -------------------------------------------------------------------------- */

export interface CartDropContext {
  reason: CartDropReason;
  /** The order being let go of. Ids are safe to report; tokens never are. */
  orderId: string;
  /** Its status, when the fetch answered. Absent on the 404/401/403 path. */
  orderStatus?: string | null;
  /** Its age in days, when createdAt was readable. See orderAgeDays. */
  ageDays?: number | null;
  /** Which of the four stores the session had been restored from. */
  restoredFrom: OrderSessionSource | "none";
  /** Which restore did it. There are two, and they have disagreed before. */
  origin: "provider-restore" | "home-restore";
  /** The HTTP status behind a definitive-404-401-403 drop. */
  httpStatus?: number;
}

/**
 * Orders already reported as dropped, for the lifetime of this page load.
 *
 * OrderProvider's restore and the home page's own restore both run on the home
 * page and can both decide to drop the same order on the same load. That is one
 * incident, and reporting it twice would make the counts useless. Keyed by order
 * id because a drop happens once per order and never repeats — once dropped
 * there is nothing left in any store to restore, so this cannot suppress a
 * second, genuinely different event.
 */
const reportedCartDrops = new Set<string>();

/**
 * Fixed titles, one per reason, so Sentry files them as three issues.
 *
 * One shared title with a `reason` tag would group all three together, and they
 * do not belong together: two of these are correct behaviour and form the
 * denominator, and the third is the one to look at.
 */
const CART_DROP_TITLES: Record<CartDropReason, string> = {
  finalized: "Restored cart dropped: order already finalized",
  age: "Restored cart dropped: past the age ceiling",
  "definitive-404-401-403": "Restored cart dropped: server refused the session",
};

/**
 * A restored cart was thrown away.
 *
 * This is the event three of the four bugs above needed and did not have. The
 * drop is invisible to the customer at the moment it happens — they simply find
 * an empty cart later — and invisible to us, because every branch that does it
 * is a success path.
 *
 * Severity follows how much doubt there is that the drop was right:
 *
 *   finalized  info    a completed purchase is not a cart. Correct, and the
 *                      denominator: "how often is this right" is the question
 *                      the other two are measured against.
 *   age        info    correct by policy, but the AGE is the thing to read. A
 *                      drop at 5.2 days against a 5-day ceiling is the policy
 *                      working; a drop at 2.1 days means a store expired
 *                      underneath the ceiling, which is precisely the inert
 *                      policy change bca7d728 fixed and nothing could see.
 *   404/401/403 warning "could not prove ownership" is not "the order is dead".
 *                      This is the one that has stranded live carts.
 *
 * Volume is bounded by ORDERS, not page loads: a cart can only be dropped once,
 * because the drop clears every store it could be restored from.
 */
export function reportCartDropped(ctx: CartDropContext): void {
  try {
    if (!ctx.orderId) return;
    // One incident, one event, however many restores noticed it.
    if (reportedCartDrops.has(ctx.orderId)) return;
    reportedCartDrops.add(ctx.orderId);

    Sentry.captureMessage(CART_DROP_TITLES[ctx.reason], {
      level: ctx.reason === "definitive-404-401-403" ? "warning" : "info",
      tags: {
        area: "order-loss",
        dropReason: ctx.reason,
        origin: ctx.origin,
        // The store that still had a whole session, and therefore — by
        // elimination — the ones that had already lost it.
        restoredFrom: ctx.restoredFrom,
        // "unknown" rather than an absent tag: an absent tag cannot be filtered
        // on in Sentry, and "we never learned the status" is itself a finding.
        orderStatus: ctx.orderStatus || "unknown",
        httpStatus: ctx.httpStatus == null ? "none" : String(ctx.httpStatus),
      },
      extra: {
        orderId: ctx.orderId,
        // Rounded to 0.1 day upstream. Precise enough to check against the
        // ceiling, coarse enough not to single a customer out.
        ageDays: ctx.ageDays ?? null,
      },
    });
  } catch {
    // Diagnostics must never be why a customer's cart behaves differently.
  }
}

export interface PhotoGroupIdentificationContext {
  /**
   * `degraded` — no baseline, so the LAST group in the response was guessed.
   * `none`     — cannot be named at all; the caller refuses to attach photos.
   */
  confidence: "degraded" | "none";
  orderId: string;
  /** The group we settled on, when there was one. */
  orderPhotoId?: string | null;
  /** Did we have a "before" picture at all? `false` is the bug's signature. */
  baselineKnown: boolean;
  baselineCount?: number | null;
  /** Was the React Query cache populated? It is keyed on the CONTEXT pair. */
  hadOrderInCache: boolean;
  responseGroupCount?: number | null;
  /** Which store the upload's session came from. */
  tokenSource: OrderSessionSource | "none";
}

const PHOTO_GROUP_TITLES: Record<"degraded" | "none", string> = {
  degraded: "Photo group identified without a baseline (guessed)",
  none: "Photo group could not be identified after creation",
};

/**
 * add-order-photos returned, and we could not say for certain which group it
 * made.
 *
 * THIS IS BUG 1'S SIGNATURE, and the reason it ran for six occurrences with
 * nothing in Sentry. `order?.orderPhotos || []` turned "we do not know what this
 * order held" into "it held nothing", the diff then named orderPhotos[0] — the
 * OLDEST group — and every photo was registered against it. Nothing threw. The
 * customer saw an empty line item and an empty drawer.
 *
 * `baselineKnown: false` is the exact condition that produced it, and it is now
 * a tag. `hadOrderInCache` and `tokenSource` together name the cause: a session
 * resolved from anywhere but `context` while the cache is empty is the
 * divergence between the two readers.
 *
 * Not captureException, though the caller does go on to throw: the stack would
 * be the upload handler either way and tells us nothing. The decision does.
 */
export function reportPhotoGroupIdentification(
  ctx: PhotoGroupIdentificationContext,
): void {
  try {
    Sentry.captureMessage(PHOTO_GROUP_TITLES[ctx.confidence], {
      // `none` refuses the upload outright, so the customer is blocked and the
      // batch is lost — an error. `degraded` still attaches, to a group that is
      // very probably right; worth seeing, not worth an alert.
      level: ctx.confidence === "none" ? "error" : "warning",
      tags: {
        area: "photo-upload",
        confidence: ctx.confidence,
        // The `|| []` that caused this. If it is ever "false" on a healthy
        // order, the baseline plumbing has regressed.
        baselineKnown: String(ctx.baselineKnown),
        hadOrderInCache: String(ctx.hadOrderInCache),
        tokenSource: ctx.tokenSource,
      },
      extra: {
        orderId: ctx.orderId,
        orderPhotoId: ctx.orderPhotoId ?? null,
        baselineCount: ctx.baselineCount ?? null,
        responseGroupCount: ctx.responseGroupCount ?? null,
      },
    });
  } catch {
    // best effort
  }
}

export interface EmptyPhotoGroupContext {
  orderId: string;
  /** The group created by this run, named exactly. Never a guess. */
  orderPhotoId: string;
  /** How many files the customer had picked. A COUNT — never their names. */
  fileCount: number;
}

/**
 * A group was created and the upload registered nothing against it.
 *
 * The other half of bug 1, seen from the order's side: staging order 7f69e9c2
 * had seven groups and six of them were empty. An empty group is not harmless —
 * it renders as a cart line worth nothing, it makes the order look non-empty to
 * every later check, and the backend refuses payment on it ("the photos for this
 * order never finished uploading").
 *
 * The upload throws when it attaches nothing, so an exception is already on its
 * way to Sentry from the surrounding catch. That exception says the upload
 * failed; it does not say a group was left behind or WHICH one, and the group is
 * what someone has to go and look at. Reported at warning, because the caller
 * then tries to clean it up and usually succeeds.
 */
export function reportEmptyPhotoGroup(ctx: EmptyPhotoGroupContext): void {
  try {
    Sentry.captureMessage("Upload attached no photos to the group it created", {
      level: "warning",
      tags: { area: "photo-upload" },
      extra: {
        orderId: ctx.orderId,
        orderPhotoId: ctx.orderPhotoId,
        fileCount: ctx.fileCount,
      },
    });
  } catch {
    // best effort
  }
}

/** Test seam: drops the per-page-load dedupe of cart-drop reports. */
export function resetCartDropReportsForTests(): void {
  reportedCartDrops.clear();
}
